import { ReactNode, createContext, useContext } from 'react'

import { Card } from './Card'
import { ClickableDeprecated } from './ClickableDeprecated'
import { Column } from './Column'
import { Icon, IconName } from './Icon'
import { Row } from './Row'
import { Text, TextProps } from './Text'
import { Color, theme } from '../constants'

type SmallCardStyle = {
  color: Color
  hoverBackgroundColor: Color
  lightColor?: Color
}

const DEFAULT_DESIGN = 'default'

const ITEM_STYLES: Record<SmallCardDesign, SmallCardStyle> = {
  default: {
    color: Color.DARK_BLUE,
    hoverBackgroundColor: Color.LIGHTEST_BLUE,
    lightColor: Color.MEDIUM_BLUE,
  },
  error: {
    color: Color.RED,
    hoverBackgroundColor: Color.LIGHTER_RED,
    lightColor: Color.MEDIUM_RED,
  },
  success: {
    color: Color.DARKER_GREEN,
    hoverBackgroundColor: Color.LIGHTER_GREEN,
    lightColor: Color.DARK_GREEN,
  },
}

const SmallCardContext = createContext<SmallCardProps>({ title: '' })

const getTextSize = (small?: boolean) => (small ? 6 : undefined)

const Description = (props: TextProps) => {
  const rootProps = useContext(SmallCardContext)
  const style = ITEM_STYLES[rootProps.design ?? DEFAULT_DESIGN]
  return (
    <Text
      color={style.lightColor}
      lineHeight={1}
      noSelect
      size={6}
      {...props}
    />
  )
}

const Subtitle = (props: TextProps) => {
  const rootProps = useContext(SmallCardContext)
  const textSize = getTextSize(rootProps.small)
  return <Text lineHeight={1} noSelect size={textSize} {...props} />
}

export type SmallCardDesign = 'default' | 'error' | 'success'

export type SmallCardProps = {
  buttonDesign?: SmallCardDesign
  buttonIcon?: IconName
  className?: string
  description?: ReactNode
  design?: SmallCardDesign
  icon?: IconName
  onButtonClick?: () => void
  onClick?: () => void
  small?: boolean
  subtitle?: ReactNode
  title: string
}

export type SmallCardTitleProps = {
  children: ReactNode
}

export const SmallCard = (props: SmallCardProps) => {
  const design = props.design ?? DEFAULT_DESIGN
  const textSize = getTextSize(props.small)

  const style = ITEM_STYLES[design]
  const buttonStyle = ITEM_STYLES[props.buttonDesign ?? design]

  return (
    <SmallCardContext.Provider value={props}>
      <Card boxShadow="none" className={props.className}>
        <Row
          alignItems="center"
          gridGap={3}
          justifyContent="space-between"
          p={props.small ? 1 : 2}
        >
          <ClickableDeprecated
            borderRadius={theme.border.radius.normal}
            flexGrow={2}
            hoverBackgroundColor={style.hoverBackgroundColor}
            onClick={props.onClick}
            overflow="hidden"
            px={2}
          >
            <Row
              alignItems="center"
              color={style.color}
              gridGap={3}
              textAlign="left"
            >
              {props.icon && (
                <Icon
                  backgroundColor={style.hoverBackgroundColor}
                  name={props.icon}
                  round
                  size={props.small ? 'md-plus' : 'lg'}
                />
              )}
              <Column gridGap={2} py={2}>
                <Row alignItems="center" gridGap={2}>
                  <Text bold lineHeight={1} noSelect size={textSize}>
                    {props.title}
                  </Text>
                  {props.subtitle &&
                    (typeof props.subtitle === 'string' ? (
                      <Subtitle>{props.subtitle}</Subtitle>
                    ) : (
                      props.subtitle
                    ))}
                </Row>
                {props.description &&
                  (typeof props.description === 'string' ? (
                    <Description>{props.description}</Description>
                  ) : (
                    props.description
                  ))}
              </Column>
            </Row>
          </ClickableDeprecated>
          {props.buttonIcon && (
            <ClickableDeprecated
              borderRadius={theme.border.radius.normal}
              color={buttonStyle.color}
              flexShrink={0}
              hoverBackgroundColor={buttonStyle.hoverBackgroundColor}
              onClick={props.onButtonClick}
              overflow="hidden"
              p={1}
            >
              <Icon name={props.buttonIcon} />
            </ClickableDeprecated>
          )}
        </Row>
      </Card>
    </SmallCardContext.Provider>
  )
}

SmallCard.Description = Description
SmallCard.Subtitle = Subtitle
